<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'dashboard/composables/store.js';
import { useAlert } from 'dashboard/composables';
import ButtonV4 from 'next/button/Button.vue';

const store = useStore();
const plans = ref([]);
const currentPlan = ref('');
const isComplimentary = ref(false);
const isLoading = ref(true);
const changingPlan = ref('');

const emit = defineEmits(['planChanged']);

const fetchPlans = async () => {
  isLoading.value = true;
  try {
    const data = await store.dispatch('accounts/getAvailablePlans');
    plans.value = data.plans || [];
    currentPlan.value = data.current_plan || '';
    isComplimentary.value = data.complimentary || false;
  } catch {
    useAlert('Erro ao carregar planos');
  } finally {
    isLoading.value = false;
  }
};

const formatPrice = (amount, currency) => {
  if (amount === 0) return 'Gratis';
  const value = amount / 100;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: (currency || 'brl').toUpperCase(),
  }).format(value);
};

const isCurrentPlan = name => currentPlan.value === name;

const isPlanHigher = name => {
  const order = ['Hacker', 'Startups', 'Business', 'Enterprise'];
  return order.indexOf(name) > order.indexOf(currentPlan.value);
};

const planStyle = name => {
  if (name === 'Enterprise') return 'bg-gradient-to-br from-violet-500/10 to-indigo-500/10 border-violet-300 dark:border-violet-700';
  if (name === 'Business') return 'bg-gradient-to-br from-blue-500/10 to-cyan-500/10 border-blue-300 dark:border-blue-700';
  if (name === 'Startups') return 'bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border-emerald-300 dark:border-emerald-700';
  return 'bg-n-solid-2 border-n-weak';
};

const onChangePlan = async planName => {
  if (isCurrentPlan(planName) || changingPlan.value) return;
  changingPlan.value = planName;
  try {
    await store.dispatch('accounts/changePlan', planName);
    currentPlan.value = planName;
    useAlert(`Plano alterado para ${planName}`);
    emit('planChanged');
  } catch {
    // error handled by store
  } finally {
    changingPlan.value = '';
  }
};

onMounted(fetchPlans);
</script>

<template>
  <div>
    <div v-if="isLoading" class="py-8 text-center text-n-slate-11">
      Carregando planos...
    </div>
    <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <div
        v-for="plan in plans"
        :key="plan.name"
        class="relative rounded-xl border p-5 flex flex-col justify-between transition-all"
        :class="[
          planStyle(plan.name),
          isCurrentPlan(plan.name) ? 'ring-2 ring-n-brand' : '',
        ]"
      >
        <div
          v-if="isCurrentPlan(plan.name)"
          class="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-n-brand text-white text-xs font-semibold px-3 py-0.5 rounded-full"
        >
          Plano atual
        </div>
        <div>
          <h3 class="text-lg font-semibold text-n-slate-12 mt-1">
            {{ plan.name }}
          </h3>
          <div class="mt-2 mb-4">
            <span class="text-2xl font-bold text-n-slate-12">
              {{ formatPrice(plan.amount, plan.currency) }}
            </span>
            <span
              v-if="plan.amount > 0"
              class="text-sm text-n-slate-11"
            >
              /{{ plan.interval === 'month' ? 'mes' : plan.interval }}
            </span>
          </div>
          <ul class="space-y-1.5 mb-5">
            <li
              v-for="feature in plan.features"
              :key="feature"
              class="flex items-start gap-2 text-sm text-n-slate-11"
            >
              <span class="text-n-brand mt-0.5 flex-shrink-0">&#10003;</span>
              {{ feature }}
            </li>
          </ul>
        </div>
        <ButtonV4
          v-if="!isCurrentPlan(plan.name)"
          sm
          :solid="isPlanHigher(plan.name)"
          :flushed="!isPlanHigher(plan.name)"
          :blue="isPlanHigher(plan.name)"
          :slate="!isPlanHigher(plan.name)"
          :is-loading="changingPlan === plan.name"
          :disabled="!!changingPlan"
          class="w-full justify-center"
          @click="onChangePlan(plan.name)"
        >
          {{ isPlanHigher(plan.name) ? 'Fazer upgrade' : 'Mudar plano' }}
        </ButtonV4>
        <div
          v-else
          class="text-center text-sm font-medium text-n-brand py-1.5"
        >
          &#10003; Ativo
        </div>
      </div>
    </div>
    <p
      v-if="isComplimentary"
      class="mt-3 text-xs text-n-slate-11"
    >
      Sua conta e cortesia - alteracoes de plano nao geram cobranca.
    </p>
  </div>
</template>
